import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '../Button';
import Empty from '../Empty';
import FaqForm from '../FaqForm';
import FaqsRepeater from './FaqsRepeater';
import FontAwesomeIcon from '../Icon';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { getUrlExtension, isImage } from 'src/helpers/CommonHelpers';
import { FaqDocument, getDisplayQuestion } from 'src/components/Faqs/types';
import { FaqFormData } from 'src/components/Faqs/CustomHooks/useFaqForm';
import './Faqs.scss';
import Search from 'src/components/Search/Search';

interface FaqsProps {
  // Search props.
  faqs: FaqDocument[];
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchQuery?: string;
  // Delete prop.
  onDeleteFaq: (faq: FaqDocument) => void;
  hideActions?: boolean;
  // Form props.
  formData: FaqFormData;
  setFormData: (data: Partial<FaqFormData>) => void;
  isEditing: boolean;
  showOverlay: boolean;
  onOpenForm: () => void;
  onCloseForm: () => void;
  onEditFaq: (faq: FaqDocument) => void;
  onSaveFaq: (faq: any, action: 'SaveAndClose' | 'SaveAndAdd') => void;
  isSaving?: boolean;
}

const Faqs = ({
  faqs,
  onSearch,
  searchQuery = '',
  onDeleteFaq,
  hideActions,
  formData,
  setFormData,
  isEditing,
  showOverlay,
  onOpenForm,
  onCloseForm,
  onEditFaq,
  onSaveFaq,
  isSaving = false,
}: FaqsProps) => {
  const user: any = useSelector(selectAuthenticatedUser);

  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  const role: string = user.roles[0].role.name;
  const hideAddFaqButton: boolean =
    role === 'Home Owner' || role === 'Strata Manager' || role === 'Realtor';

  const onImageClicked = (_index: number, image: string): void => {
    if (!image) return;

    const extension: string | null = getUrlExtension(image);
    const shouldOpenInNewTab: boolean =
      !isImage(image) || extension === 'heic';

    if (shouldOpenInNewTab) {
      window.open(image, '_blank')?.focus();
      return;
    }

    setSelectedImage(image);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = (): void => {
    setLightboxOpen(false);
    setSelectedImage('');
  };

  return (
    <div className="faqs">
      <div className="faqs-header">
        <div className="faqs-search">
          <Search
            size="small"
            placeholder="Search FAQs..."
            onChange={onSearch}
            theme={undefined}
          />
        </div>
        {hideAddFaqButton ? null : (
          <Button
            color="teal"
            label="Add Faq"
            onClick={onOpenForm}
            icon={<FontAwesomeIcon glyph="edit" weight="light" size="small" />}
            size="small"
          />
        )}
      </div>
      {faqs.length > 0 ? (
        <FaqsRepeater
          onEdit={onEditFaq}
          hideActions={hideActions}
          user={user}
          onDelete={(faq: FaqDocument) =>
            window.confirm(
              `Are you sure you want to delete ${getDisplayQuestion(faq)}?`
            ) && onDeleteFaq(faq)
          }
          faqs={faqs}
          onImageClicked={onImageClicked}
        />
      ) : (
        <Empty
          headline="No FAQs yet"
          strapline="You can use the Faqs section to post any status updates or general comments to keep everyone on the same page."
          searchIsActive={searchQuery}
          cta={
            hideAddFaqButton ? null : (
              <Button
                color="teal"
                label="Add Faq"
                onClick={onOpenForm}
                icon={
                  <FontAwesomeIcon glyph="edit" weight="light" size="small" />
                }
                size="small"
              />
            )
          }
          image={<img alt="No FAQs yet" src="/assets/empty/notes.svg" />}
        />
      )}
      <ReusableLightbox
        isOpen={lightboxOpen}
        onClose={handleCloseLightbox}
        image={selectedImage}
      />
      <GenericDialogue
        isOpen={showOverlay}
        onClose={onCloseForm}
        title={isEditing ? 'Edit FAQ' : 'Add FAQ'}
        description={
          isEditing
            ? 'Update the question, answer, and attachments for this FAQ.'
            : 'Add a new question, answer, and any supporting attachments.'
        }
        maxWidth="140rem"
      >
        <FaqForm
          formData={formData}
          setFormData={setFormData}
          isEditing={isEditing}
          onSaveFaq={onSaveFaq}
          onClose={onCloseForm}
          isSaving={isSaving}
        />
      </GenericDialogue>
    </div>
  );
};

export default Faqs;
